import type { ReactNode } from 'react';
import { View } from 'tamagui';

/**
 * PSCircleButton — a circular pressable that centers an icon (atom).
 *
 * The round back / share / account / social control from the designs, sized to
 * match PSButton's height. Kept props-thin (CLAUDE.md "Component Complexity"):
 * two variants — variants get added only when a real call site diverges.
 *
 * `variant`:
 *  - `normal` (default): hairline border + optional fill, for the cases this
 *    sits in plain content (no header behind it) and needs its own edge.
 *  - `bare`: no border, no background, just the icon — for headers, where the
 *    header itself already provides the surface. `filled`/`backgroundColor`/
 *    `borderColor` don't apply here, so the type forbids passing them —
 *    a props-that-would-be-silently-ignored footgun the type system catches
 *    (same trick as PSButton's `loading`/`loadingTitle` pair).
 *
 * Icon-agnostic on purpose: the caller passes the glyph as `children`. This
 * codebase ships `@expo/vector-icons` (not the sample's lucide), and the atom
 * shouldn't pin an icon library — it owns the circle, the caller owns the icon.
 */
type PSCircleButtonProps = {
  children: ReactNode;
  onPress?: () => void;
  disabled?: boolean;
} & (
  | {
      variant?: 'normal';
      /** Solid fill vs transparent. */
      filled?: boolean;
      backgroundColor?: '$background' | '$brand';
      /** Defaults to the standard hairline gray — TicketTier's stepper is the
       * one caller that swaps it for brand while the button is actually
       * pressable. */
      borderColor?:
        | '$borderColor'
        | '$color.brand'
        | '$color.brandSecondary'
        | '$color.panelTranslucent';
    }
  | {
      variant: 'bare';
      filled?: never;
      backgroundColor?: never;
      borderColor?: never;
    }
);

export default function PSCircleButton({
  children,
  onPress,
  disabled = false,
  variant = 'normal',
  filled = false,
  backgroundColor,
  borderColor = '$borderColor',
}: PSCircleButtonProps) {
  const isBare = variant === 'bare';

  return (
    <View
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      cursor={disabled || !onPress ? undefined : 'pointer'}
      opacity={disabled ? 0.35 : 1}
      pressStyle={onPress && !disabled ? { opacity: 0.85 } : undefined}
      height={38}
      width={38}
      rounded="$base"
      borderWidth={isBare ? 0 : '$borderWidth.hairline'}
      borderColor={isBare ? undefined : borderColor}
      backgroundColor={
        isBare
          ? '$transparent'
          : filled
            ? (backgroundColor ?? '$background')
            : '$transparent'
      }
      items="center"
      justify="center"
    >
      {children}
    </View>
  );
}
